import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import axios from 'axios';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Layout } from './components/Layout';
import { Landing } from './pages/Landing';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { AuthCallback } from './pages/AuthCallback';
import { Dashboard } from './pages/Dashboard';
import { Questionnaire } from './pages/Questionnaire';
import { Results } from './pages/Results';
import { MotivationTest } from './pages/MotivationTest';
import { MotivationResults } from './pages/MotivationResults';
import { ProchaskaTest } from './pages/ProchaskaTest';
import { ProchaskaResults } from './pages/ProchaskaResults';
import { LungScreeningTest } from './pages/LungScreeningTest';
import { LungScreeningResults } from './pages/LungScreeningResults';
import { WeeklyTracking } from './pages/WeeklyTracking';
import { Statistics } from './pages/Statistics';
import { Exercises } from './pages/Exercises';
import { ForgotPassword, ResetPassword } from './pages/PasswordReset';
import { Settings } from './pages/Settings';
import { Videos } from './pages/Videos';
import { Subscription } from './pages/Subscription';
import { PaymentSuccess, PaymentCancel } from './pages/PaymentStatus';
import './App.css';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected Route Component - Requires lung screening first and blocks non-smokers
const ProtectedRoute = ({ children, requireScreening = true }) => {
  const { isAuthenticated, loading, logout } = useAuth();
  const [screeningStatus, setScreeningStatus] = useState(null);
  const [screeningDetails, setScreeningDetails] = useState(null);
  const [checkingScreening, setCheckingScreening] = useState(true);

  useEffect(() => {
    const checkScreeningStatus = async () => {
      if (isAuthenticated && requireScreening) {
        try {
          const [statusRes, latestRes] = await Promise.all([
            axios.get(`${API_URL}/lung-screening/status`),
            axios.get(`${API_URL}/lung-screening/latest`)
          ]);
          setScreeningStatus(statusRes.data);
          setScreeningDetails(latestRes.data);
        } catch (error) {
          console.error('Failed to check screening status:', error);
        }
      }
      setCheckingScreening(false);
    };

    if (!loading) {
      checkScreeningStatus();
    }
  }, [isAuthenticated, loading, requireScreening]);

  if (loading || checkingScreening) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If screening is required but not completed, redirect to screening
  if (requireScreening && screeningStatus && !screeningStatus.completed) {
    return <Navigate to="/lung-screening" replace />;
  }

  // If user is a non-smoker or ex-smoker, block access to the app
  if (requireScreening && screeningStatus && screeningStatus.completed && screeningStatus.is_smoker === false) {
    const isExSmoker = screeningDetails?.has_quit === true;
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB] p-4">
        <div className="max-w-md text-center">
          <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${isExSmoker ? 'bg-green-100' : 'bg-green-100'}`}>
            {isExSmoker ? (
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {isExSmoker ? 'Félicitations !' : 'Félicitations !'}
          </h2>
          <p className="text-gray-600 mb-6">
            {isExSmoker 
              ? "Félicitations pour avoir arrêté de fumer ! Cette application est destinée aux fumeurs actifs souhaitant arrêter. Vous n'avez pas accès à l'application car vous avez déjà arrêté."
              : "Félicitations, vous n'avez jamais fumé ! Continuez sur cette voie en pratiquant une activité physique régulière."}
          </p>
          <button
            onClick={() => logout()}
            className={`px-6 py-3 rounded-full text-white font-semibold shadow-lg ${
              isExSmoker ? 'bg-green-600 hover:bg-green-700' : 'bg-green-600 hover:bg-green-700'
            }`}
            data-testid="non-smoker-logout-btn"
          >
            Se déconnecter
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// Screening Route - Only for authenticated users who haven't completed screening
const ScreeningRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const [screeningStatus, setScreeningStatus] = useState(null);
  const [checkingScreening, setCheckingScreening] = useState(true);

  useEffect(() => {
    const checkScreeningStatus = async () => {
      if (isAuthenticated) {
        try {
          const response = await axios.get(`${API_URL}/lung-screening/status`);
          setScreeningStatus(response.data);
        } catch (error) {
          console.error('Failed to check screening status:', error);
        }
      }
      setCheckingScreening(false);
    };

    if (!loading) {
      checkScreeningStatus();
    }
  }, [isAuthenticated, loading]);

  if (loading || checkingScreening) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If screening already completed, redirect to dashboard
  if (screeningStatus && screeningStatus.completed) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9FAFB]">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const location = useLocation();
  // Handle Emergent OAuth callback: session_id comes back in URL fragment
  // This synchronous check prevents race conditions with ProtectedRoute/PublicRoute
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } 
        />
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } 
        />
        <Route 
          path="/forgot-password" 
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          } 
        />
        <Route 
          path="/reset-password" 
          element={
            <PublicRoute>
              <ResetPassword />
            </PublicRoute>
          } 
        />
        <Route 
          path="/lung-screening" 
          element={
            <ScreeningRoute>
              <LungScreeningTest />
            </ScreeningRoute>
          } 
        />
        <Route 
          path="/lung-screening-results" 
          element={
            <ProtectedRoute requireScreening={false}>
              <LungScreeningResults />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/questionnaire" 
          element={
            <ProtectedRoute>
              <Questionnaire />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/results" 
          element={
            <ProtectedRoute>
              <Results />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/motivation-test" 
          element={
            <ProtectedRoute>
              <MotivationTest />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/motivation-results" 
          element={
            <ProtectedRoute>
              <MotivationResults />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/prochaska-test" 
          element={
            <ProtectedRoute>
              <ProchaskaTest />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/prochaska-results" 
          element={
            <ProtectedRoute>
              <ProchaskaResults />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/weekly-tracking" 
          element={
            <ProtectedRoute>
              <WeeklyTracking />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/statistics" 
          element={
            <ProtectedRoute>
              <Statistics />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/exercises" 
          element={
            <ProtectedRoute>
              <Exercises />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/videos" 
          element={
            <ProtectedRoute>
              <Videos />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/settings" 
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/subscription" 
          element={
            <ProtectedRoute requireScreening={false}>
              <Subscription />
            </ProtectedRoute>
          } 
        />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-center" 
            richColors 
            toastOptions={{
              className: 'rounded-xl',
            }}
          />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
